/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import {useEffect, useRef, useState, type ReactNode} from "react";
import {AnankeSelect} from "@vesta/ananke/components/anankeSelect";
import {clsx} from "~/components/table/tableUtils.tsx";

type BaseFooterProps = {
    totalRows: number | null
    totalRowsStatus?: "pending" | "error" | "success"
    totalRowsError?: Error | null
    loadedRowCount: number
    pageSizeOptions?: number[]
    pageSize: number
    onPageSizeChange: (size: number) => void
    isLoading?: boolean
    className?: string
    variant?: "default" | "page"
}

type PaginatedFooterProps = BaseFooterProps & {
    mode: "paginated"
    currentPage: number
    totalPages: number
    onFirstPage: () => void
    onPreviousPage: () => void
    onNextPage: () => void
    onLastPage: () => void
    onGoToPage?: (page: number) => void
    isFirstPage: boolean
    isLastPage: boolean
}

type InfiniteFooterProps = BaseFooterProps & {
    mode: "infinite"
    hasNextPage: boolean
    onLoadMore: () => void
}

export type TableFooterProps = PaginatedFooterProps | InfiniteFooterProps

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

function getCompactPageItems(currentPage: number, totalPages: number): Array<number | "ellipsis"> {
    if (totalPages <= 1) {
        return [1];
    }
    if (totalPages <= 5) {
        return Array.from({length: totalPages}, (_, index) => index + 1);
    }

    const items: Array<number | "ellipsis"> = [1];
    const showLeftEllipsis = currentPage > 3;

    if (showLeftEllipsis) {
        items.push("ellipsis");
    } else {
        for (let page = 2; page < currentPage; page++) {
            items.push(page);
        }
    }

    if (currentPage !== 1 && currentPage !== totalPages) {
        items.push(currentPage);
    }

    const showRightEllipsis = currentPage < totalPages - 2;

    if (showRightEllipsis) {
        items.push("ellipsis");
    } else {
        for (let page = currentPage + 1; page < totalPages; page++) {
            items.push(page);
        }
    }

    if (totalPages > 1) {
        items.push(totalPages);
    }

    return items;
}

export function TableFooter(props: TableFooterProps) {
    const {
        mode,
        totalRows,
        totalRowsStatus = "success",
        totalRowsError,
        loadedRowCount,
        pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
        pageSize,
        onPageSizeChange,
        isLoading,
        className,
        variant = "default",
    } = props;
    const currentPage = mode === "paginated" ? props.currentPage : 1;
    const totalPages = mode === "paginated" ? props.totalPages : 1;
    const [pageInputValue, setPageInputValue] = useState(String(currentPage).padStart(2, "0"));
    const [isEditingPage, setIsEditingPage] = useState(false);
    const pageInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditingPage !== true) {
            setPageInputValue(String(currentPage).padStart(2, "0"));
        }
    }, [currentPage, isEditingPage]);

    const handlePageInputBlur = () => {
        setIsEditingPage(false);
        const parsed = parseInt(pageInputValue, 10);
        if (!isNaN(parsed) && parsed >= 1 && parsed <= totalPages && mode === "paginated") {
            props.onGoToPage?.(parsed);
        } else {
            setPageInputValue(String(currentPage).padStart(2, "0"));
        }
    };

    const rangeStart = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
    const rangeEnd = Math.min(currentPage * pageSize, totalRows ?? loadedRowCount);
    const compactPageItems =
        mode === "paginated" ? getCompactPageItems(currentPage, totalPages) : [];

    return (
        <div
            className={clsx(
                "table-footer",
                variant === "page" &&
                    clsx(
                        "table-footer--page w-full min-w-0 max-w-none shrink-0",
                        "box-border min-h-[58px] items-center max-sm:min-h-0 max-sm:h-auto",
                        "rounded-none border-0 border-solid",
                        "bg-ds-surface-raised",
                        "px-ds-space-100 py-0 max-sm:px-ds-space-150 md:px-ds-space-300",
                    ),
                className,
            )}
            role="contentinfo"
            aria-label="Table pagination"
        >
            <div
                className={clsx(
                    "table-footer__left",
                    variant === "page" && "max-sm:pl-0 pl-ds-space-200 sm:pl-ds-space-300",
                )}
            >
                <span className="table-footer__view-label">View</span>
                <PageSizeSelect value={pageSize} options={pageSizeOptions} onChange={onPageSizeChange} disabled={isLoading} />
            </div>

            <div className="table-footer__center">
                {mode === "paginated" ? (
                    <>
                        <div className="table-footer__pagination-controls table-footer__pagination-controls--desktop">
                            <div className="table-footer__nav-group">
                                <NavButton
                                    className="table-footer__nav-btn--ghost"
                                    onClick={props.onFirstPage}
                                    disabled={props.isFirstPage || isLoading}
                                    title="First page"
                                    aria-label="Go to first page"
                                >
                                    <DoubleChevronLeftIcon />
                                </NavButton>
                                <NavButton
                                    className="table-footer__nav-btn--ghost"
                                    onClick={props.onPreviousPage}
                                    disabled={props.isFirstPage || isLoading}
                                    title="Previous page"
                                    aria-label="Go to previous page"
                                >
                                    <ChevronLeftIcon />
                                </NavButton>
                            </div>
                            <div className="table-footer__page-info">
                                <span className="table-footer__page-label">Page</span>
                                <input
                                    ref={pageInputRef}
                                    className="table-footer__page-input"
                                    value={isEditingPage ? pageInputValue : String(currentPage).padStart(2, "0")}
                                    onChange={(e) => setPageInputValue(e.target.value.replace(/\D/g, ""))}
                                    onFocus={() => {
                                        setIsEditingPage(true);
                                        setPageInputValue(String(currentPage));
                                        setTimeout(() => pageInputRef.current?.select(), 0);
                                    }}
                                    onBlur={handlePageInputBlur}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {pageInputRef.current?.blur();}
                                        if (e.key === "Escape") {
                                            setIsEditingPage(false);
                                            setPageInputValue(String(currentPage).padStart(2, "0"));
                                            pageInputRef.current?.blur();
                                        }
                                    }}
                                    disabled={isLoading || !props.onGoToPage}
                                    aria-label="Current page"
                                    inputMode="numeric"
                                />
                                <span className="table-footer__page-of">of</span>
                                <span className="table-footer__page-total">{isLoading && totalPages === 0 ? "-" : totalPages}</span>
                            </div>
                            <div className="table-footer__nav-group">
                                <NavButton
                                    className="table-footer__nav-btn--ghost"
                                    onClick={props.onNextPage}
                                    disabled={props.isLastPage || isLoading}
                                    title="Next page"
                                    aria-label="Go to next page"
                                >
                                    <ChevronRightIcon />
                                </NavButton>
                                <NavButton
                                    className="table-footer__nav-btn--ghost"
                                    onClick={props.onLastPage}
                                    disabled={props.isLastPage || isLoading}
                                    title="Last page"
                                    aria-label="Go to last page"
                                >
                                    <DoubleChevronRightIcon />
                                </NavButton>
                            </div>
                        </div>
                        <div className="table-footer__pagination-controls table-footer__pagination-controls--mobile">
                            <NavButton
                                className="table-footer__nav-btn--ghost"
                                onClick={props.onPreviousPage}
                                disabled={props.isFirstPage || isLoading}
                                title="Previous page"
                                aria-label="Go to previous page"
                            >
                                <ChevronLeftIcon />
                            </NavButton>
                            <div className="table-footer__page-numbers">
                                {compactPageItems.map((item, index) =>
                                    item === "ellipsis" ? (
                                        <span
                                            key={`ellipsis-${String(index)}`}
                                            className="table-footer__page-ellipsis"
                                            aria-hidden
                                        >
                                            ...
                                        </span>
                                    ) : (
                                        <PageNumberButton
                                            key={item}
                                            page={item}
                                            isActive={item === currentPage}
                                            disabled={isLoading === true || props.onGoToPage === undefined}
                                            onClick={() => props.onGoToPage?.(item)}
                                        />
                                    ),
                                )}
                            </div>
                            <NavButton
                                className="table-footer__nav-btn--ghost"
                                onClick={props.onNextPage}
                                disabled={props.isLastPage || isLoading}
                                title="Next page"
                                aria-label="Go to next page"
                            >
                                <ChevronRightIcon />
                            </NavButton>
                        </div>
                    </>
                ) : (
                    <button
                        type="button"
                        className="table-footer__nav-btn"
                        onClick={props.onLoadMore}
                        disabled={!props.hasNextPage || isLoading}
                    >
                        {isLoading ? "Loading..." : "Load more"}
                    </button>
                )}
            </div>

            <div className="table-footer__right">
                {totalRowsStatus === "pending" ? (
                    <span className="table-footer__range table-footer__range--loading">Loading...</span>
                ) : totalRowsStatus === "error" ? (
                    <span className="table-footer__range table-footer__range--error">{totalRowsError?.message ?? "Error loading count"}</span>
                ) : totalRows !== null ? (
                    <>
                        <span className="table-footer__range table-footer__range--desktop sm:pr-ds-space-300 pr-ds-space-200">
                            {mode === "paginated"
                                ? `${String(rangeStart).padStart(2, "0")} - ${String(rangeEnd).padStart(2, "0")} of ${totalRows.toLocaleString()}`
                                : `${loadedRowCount.toLocaleString()} of ${totalRows.toLocaleString()} loaded`}
                        </span>
                        {mode === "paginated" ? (
                            <span className="table-footer__range table-footer__range--mobile">
                                {`${String(rangeStart).padStart(2, "0")} - ${String(rangeEnd).padStart(2, "0")}`}
                            </span>
                        ) : null}
                    </>
                ) : (
                    <span className="table-footer__range">{loadedRowCount.toLocaleString()} rows</span>
                )}
            </div>
        </div>
    );
}

function NavButton({
    onClick,
    disabled,
    title,
    children,
    className,
    ...rest
}: {
    onClick: () => void
    disabled?: boolean
    title?: string
    className?: string
    "aria-label"?: string
    children: ReactNode
}) {
    return (
        <button
            type="button"
            className={clsx("table-footer__nav-btn", className)}
            onClick={onClick}
            disabled={disabled}
            title={title}
            {...rest}
        >
            {children}
        </button>
    );
}

function PageNumberButton({
    page,
    isActive,
    disabled,
    onClick,
}: {
    page: number
    isActive: boolean
    disabled?: boolean
    onClick: () => void
}) {
    return (
        <button
            type="button"
            className={clsx("table-footer__page-num-btn", isActive && "table-footer__page-num-btn--active")}
            onClick={onClick}
            disabled={disabled === true || isActive}
            aria-label={`Page ${String(page)}`}
            aria-current={isActive ? "page" : undefined}
        >
            {page}
        </button>
    );
}

function PageSizeSelect({
    value,
    options,
    onChange,
    disabled,
}: {
    value: number
    options: number[]
    onChange: (v: number) => void
    disabled?: boolean
}) {
    const selectedOption = {label: String(value), value: String(value)};
    const selectOptions = options.map((option) => ({label: String(option), value: String(option)}));

    return (
        <div className="table-footer__page-size">
            <AnankeSelect
                options={selectOptions}
                value={selectedOption}
                isSearchable={false}
                isDisabled={disabled}
                menuPlacement="auto"
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                menuPosition="fixed"
                styles={{
                    menuPortal: (base: Record<string, unknown>) => ({...base, zIndex: 50}),
                    control: (base: Record<string, unknown>, state: {menuIsOpen: boolean}) => ({
                        ...base,
                        borderColor: state.menuIsOpen ? "var(--ds-border-focused)" : "var(--ds-border-input)",
                        boxShadow: state.menuIsOpen ? "0 0 0 1px var(--ds-border-focused)" : "none",
                    }),
                }}
                onChange={(option: unknown) => {
                    if (option === null || Array.isArray(option) || typeof option !== "object") {return;}
                    const optionValue = (option as {value?: unknown}).value;
                    if (typeof optionValue !== "string") {return;}
                    const parsedValue = Number(optionValue);
                    if (Number.isNaN(parsedValue)) {return;}
                    onChange(parsedValue);
                }}
            />
        </div>
    );
}

function DoubleChevronLeftIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="11 17 6 12 11 7" />
            <polyline points="18 17 13 12 18 7" />
        </svg>
    );
}

function ChevronLeftIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
        </svg>
    );
}

function ChevronRightIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
        </svg>
    );
}

function DoubleChevronRightIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="13 17 18 12 13 7" />
            <polyline points="6 17 11 12 6 7" />
        </svg>
    );
}
